import React, { useState, useMemo, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, differenceInDays, addMonths, isBefore } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Landmark, Plus, Search, Trash2, Edit2, Loader2, Check, Calendar,
  AlertTriangle, DollarSign, TrendingDown, CreditCard, FileText, Eye, Percent
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import FinanciamentoForm from "../components/financiamentos/FinanciamentoForm";
import ParcelasFinanciamento from "../components/financiamentos/ParcelasFinanciamento";
import Paginacao from "../components/shared/Paginacao";

const tiposFinanciamento = {
  custeio: { label: "Custeio", color: "bg-blue-100 text-blue-700" },
  investimento: { label: "Investimento", color: "bg-purple-100 text-purple-700" },
  comercializacao: { label: "Comercialização", color: "bg-green-100 text-green-700" },
  pronaf: { label: "PRONAF", color: "bg-amber-100 text-amber-700" },
  pronamp: { label: "PRONAMP", color: "bg-orange-100 text-orange-700" },
  fcn: { label: "FCO/FNE/FNO", color: "bg-teal-100 text-teal-700" },
  emprestimo_pessoal: { label: "Empréstimo Pessoal", color: "bg-red-100 text-red-700" },
  credito_rural: { label: "Crédito Rural", color: "bg-indigo-100 text-indigo-700" },
  outro: { label: "Outro", color: "bg-stone-100 text-stone-700" }
};

const statusConfig = {
  ativo: { label: "Ativo", color: "bg-green-100 text-green-700" },
  quitado: { label: "Quitado", color: "bg-blue-100 text-blue-700" },
  renegociado: { label: "Renegociado", color: "bg-amber-100 text-amber-700" },
  inadimplente: { label: "Inadimplente", color: "bg-red-100 text-red-700" },
  em_analise: { label: "Em Análise", color: "bg-purple-100 text-purple-700" }
};

export default function Financiamentos() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [parcelasDialogOpen, setParcelasDialogOpen] = useState(false);
  const [editingFinanciamento, setEditingFinanciamento] = useState(null);
  const [selectedFinanciamento, setSelectedFinanciamento] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("ativos");
  const [filterTipo, setFilterTipo] = useState("todos");
  const [activeTab, setActiveTab] = useState("lista");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const queryClient = useQueryClient();

  const { data: financiamentos = [], isLoading } = useQuery({
    queryKey: ['financiamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Financiamento.filter({ propriedade_id: propriedadeId }, '-data_contratacao', 200)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: parcelas = [] } = useQuery({
    queryKey: ['parcelas_financiamento', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ParcelaFinanciamento.filter({ propriedade_id: propriedadeId }, '-data_vencimento', 500)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Financiamento.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['financiamentos'] })
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // KPIs
  const kpis = useMemo(() => {
    const ativos = financiamentos.filter(f => f.status === 'ativo');
    const saldoTotal = ativos.reduce((acc, f) => acc + (f.saldo_devedor || f.valor_total || 0), 0);
    const valorContratado = financiamentos.reduce((acc, f) => acc + (f.valor_total || 0), 0);
    
    const hoje = new Date();
    const parcelasProximas = parcelas.filter(p => {
      if (p.status === 'pago') return false;
      const venc = parseISO(p.data_vencimento);
      const dias = differenceInDays(venc, hoje);
      return dias >= 0 && dias <= 30;
    });
    
    const parcelasAtrasadas = parcelas.filter(p => {
      if (p.status === 'pago') return false;
      return isBefore(parseISO(p.data_vencimento), hoje);
    });

    const valorProximasParcelas = parcelasProximas.reduce((acc, p) => acc + (p.valor_total || 0), 0);
    const valorAtrasado = parcelasAtrasadas.reduce((acc, p) => acc + (p.valor_total || 0), 0);

    return {
      totalAtivos: ativos.length,
      saldoTotal,
      valorContratado,
      parcelasProximas: parcelasProximas.length,
      valorProximasParcelas,
      parcelasAtrasadas: parcelasAtrasadas.length,
      valorAtrasado
    };
  }, [financiamentos, parcelas]);

  // Filtros
  const financiamentosFiltrados = useMemo(() => {
    let filtered = financiamentos;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(f =>
        f.instituicao?.toLowerCase().includes(term) ||
        f.numero_contrato?.toLowerCase().includes(term) ||
        f.finalidade?.toLowerCase().includes(term)
      );
    }

    if (filterStatus === "ativos") {
      filtered = filtered.filter(f => f.status === 'ativo');
    } else if (filterStatus !== "todos") {
      filtered = filtered.filter(f => f.status === filterStatus);
    }

    if (filterTipo !== "todos") {
      filtered = filtered.filter(f => f.tipo === filterTipo);
    }

    return filtered;
  }, [financiamentos, searchTerm, filterStatus, filterTipo]);

  // Paginação
  const financiamentosPaginados = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return financiamentosFiltrados.slice(start, start + pageSize);
  }, [financiamentosFiltrados, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterTipo]);

  const handleEdit = (fin) => {
    setEditingFinanciamento(fin);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingFinanciamento(null);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['financiamentos'] });
    queryClient.invalidateQueries({ queryKey: ['parcelas_financiamento'] });
    setDialogOpen(false);
    setEditingFinanciamento(null);
  };

  const handleVerParcelas = (fin) => {
    setSelectedFinanciamento(fin);
    setParcelasDialogOpen(true);
  };

  const getProgressoPagamento = (fin) => {
    if (!fin.total_parcelas) return 0;
    return ((fin.parcelas_pagas || 0) / fin.total_parcelas) * 100;
  };

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Landmark className="w-8 h-8 text-indigo-600" />
            Financiamentos e Empréstimos
          </h1>
          <p className="text-stone-600 mt-1">Gerencie seus créditos rurais e empréstimos</p>
        </div>

        <Button onClick={handleNew} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
          <Plus className="w-5 h-5" />
          Novo Financiamento
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <p className="text-indigo-100 text-sm">Contratos Ativos</p>
            <p className="text-2xl font-bold mt-1">{kpis.totalAtivos}</p>
            <p className="text-xs text-indigo-200 mt-1">financiamentos</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <p className="text-purple-100 text-sm">Saldo Devedor Total</p>
            <p className="text-xl font-bold mt-1">{formatCurrency(kpis.saldoTotal)}</p>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${kpis.parcelasAtrasadas > 0 ? 'from-red-500 to-red-600' : 'from-green-500 to-green-600'} text-white`}>
          <CardContent className="p-4">
            <p className="text-white/80 text-sm">Parcelas Atrasadas</p>
            <p className="text-2xl font-bold mt-1">{kpis.parcelasAtrasadas}</p>
            <p className="text-xs text-white/70 mt-1">{formatCurrency(kpis.valorAtrasado)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <p className="text-amber-100 text-sm">Próximos 30 dias</p>
            <p className="text-2xl font-bold mt-1">{kpis.parcelasProximas}</p>
            <p className="text-xs text-amber-200 mt-1">{formatCurrency(kpis.valorProximasParcelas)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1">
          <TabsTrigger value="lista" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Financiamentos
          </TabsTrigger>
          <TabsTrigger value="calendario" className="gap-2">
            <Calendar className="w-4 h-4" />
            Próximos Vencimentos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lista" className="mt-6 space-y-4">
          {/* Filtros */}
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  placeholder="Buscar por instituição ou contrato..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativos">Ativos</SelectItem>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="quitado">Quitados</SelectItem>
                    <SelectItem value="inadimplente">Inadimplentes</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os tipos</SelectItem>
                    {Object.entries(tiposFinanciamento).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Lista */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            {financiamentosFiltrados.length === 0 ? (
              <div className="p-12 text-center">
                <Landmark className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhum financiamento encontrado</p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-stone-100">
                  {financiamentosPaginados.map((fin) => (
                    <div key={fin.id} className="p-4 hover:bg-stone-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <p className="font-semibold text-stone-800">{fin.instituicao}</p>
                            <Badge className={tiposFinanciamento[fin.tipo]?.color}>
                              {tiposFinanciamento[fin.tipo]?.label}
                            </Badge>
                            <Badge className={statusConfig[fin.status]?.color}>
                              {statusConfig[fin.status]?.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-stone-500 mb-2">
                            Contrato: {fin.numero_contrato || '-'} • {fin.finalidade}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="text-stone-600">
                              <strong>Valor:</strong> {formatCurrency(fin.valor_total)}
                            </span>
                            <span className="text-stone-600">
                              <strong>Saldo:</strong> {formatCurrency(fin.saldo_devedor || fin.valor_total)}
                            </span>
                            <span className="text-stone-600">
                              <strong>Juros:</strong> {fin.taxa_juros}% a.a.
                            </span>
                          </div>
                          {fin.total_parcelas > 0 && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                                <span>{fin.parcelas_pagas || 0} de {fin.total_parcelas} parcelas</span>
                                <span>{getProgressoPagamento(fin).toFixed(0)}%</span>
                              </div>
                              <Progress value={getProgressoPagamento(fin)} className="h-2" />
                            </div>
                          )}
                        </div>

                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleVerParcelas(fin)}
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(fin)}
                            className="text-stone-400 hover:text-stone-600"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(fin.id)}
                            className="text-stone-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Paginacao
                  totalItems={financiamentosFiltrados.length}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="calendario" className="mt-6">
          <Card className="bg-white border-stone-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                Próximos Vencimentos de Parcelas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {parcelas.filter(p => p.status !== 'pago').length === 0 ? (
                <p className="text-stone-500 text-center py-8">Nenhuma parcela pendente</p>
              ) : (
                <div className="space-y-3">
                  {parcelas
                    .filter(p => p.status !== 'pago')
                    .sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento))
                    .slice(0, 10)
                    .map(parcela => {
                      const fin = financiamentos.find(f => f.id === parcela.financiamento_id);
                      const vencida = isBefore(parseISO(parcela.data_vencimento), new Date());
                      return (
                        <div
                          key={parcela.id}
                          className={`p-4 rounded-xl border ${vencida ? 'border-red-200 bg-red-50' : 'border-stone-200 bg-stone-50'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-stone-800">{fin?.instituicao}</p>
                              <p className="text-sm text-stone-500">Parcela {parcela.numero_parcela}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">{formatCurrency(parcela.valor_total)}</p>
                              <p className={`text-sm ${vencida ? 'text-red-600 font-medium' : 'text-stone-500'}`}>
                                {format(parseISO(parcela.data_vencimento), "dd/MM/yyyy")}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-indigo-600" />
              {editingFinanciamento ? "Editar Financiamento" : "Novo Financiamento"}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do financiamento
            </DialogDescription>
          </DialogHeader>
          <FinanciamentoForm
            financiamento={editingFinanciamento}
            safras={safras}
            onSuccess={handleSuccess}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog Parcelas */}
      <Dialog open={parcelasDialogOpen} onOpenChange={setParcelasDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Parcelas - {selectedFinanciamento?.instituicao}
            </DialogTitle>
            <DialogDescription>
              Visualize e atualize as parcelas do financiamento
            </DialogDescription>
          </DialogHeader>
          {selectedFinanciamento && (
            <ParcelasFinanciamento
              financiamento={selectedFinanciamento}
              parcelas={parcelas.filter(p => p.financiamento_id === selectedFinanciamento.id)}
              onUpdate={() => {
                queryClient.invalidateQueries({ queryKey: ['parcelas_financiamento'] });
                queryClient.invalidateQueries({ queryKey: ['financiamentos'] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
